// A board's public link shown as a web page (S4, 25 Oct 2026). The owner picks, in Share, whether the link shows
// the board as it is (Board, every link made before this) or as a clean page someone sent (Web page), and may write a
// line to go under the name. Both are the owner's alone: they change only through the share route, on the owner's
// own session (worker/workspace.ts), never through an agent's key, a board's put or a coworker.
//
// Also here: the addresses of a doc board's pages on its web page, and the head a link's page load gets, so a link
// pasted in a chat app shows the board's name and line (worker/sharePage.ts writes it).

import { visibleWords } from "./brief";
import { descendants } from "./entries";
import type { Entry, ShareSettings } from "./types";

export type ShareView = "board" | "page";

/** The line under a web page's name: one line, plain text. */
export const SHARE_LINE_MAX = 120;

/** How a link shows: an older link, with no say, shows the board. */
export function shareView(s: Pick<ShareSettings, "view"> | null | undefined): ShareView {
  return s?.view === "page" ? "page" : "board";
}

/** The owner's line, as kept: one line of plain words, without characters nobody sees, or null when there are none. */
export function cleanShareLine(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const one = visibleWords(v.replace(/[\r\n\t\u2028\u2029]+/g, " "))
    .replace(/\s+/g, " ")
    .trim();
  if (!one) return null;
  // cut by characters as people count them, never through the middle of an emoji's pair
  return Array.from(one).slice(0, SHARE_LINE_MAX).join("").trim() || null;
}

/** What the share route may change, from what a request sent; anything else it sent is ignored. */
export interface SharePatch {
  enabled?: boolean;
  slug?: string;
  badge?: boolean;
  view?: ShareView;
  /** null clears it */
  line?: string | null;
  /** a doc board's page the link shows alone, with the pages inside it; null shows the whole doc (the route checks the page is there) */
  page?: string | null;
}

export function readSharePatch(body: unknown): SharePatch {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const out: SharePatch = {};
  if (typeof b.enabled === "boolean") out.enabled = b.enabled;
  if (typeof b.slug === "string") out.slug = b.slug;
  if (typeof b.badge === "boolean") out.badge = b.badge;
  if (b.view === "board" || b.view === "page") out.view = b.view;
  if (Object.hasOwn(b, "line") && (b.line === null || typeof b.line === "string")) out.line = cleanShareLine(b.line);
  if (Object.hasOwn(b, "page") && (b.page === null || (typeof b.page === "string" && b.page.length <= 60))) out.page = b.page || null;
  return out;
}

/** A board's share settings with a patch's look applied (how it shows, its line, the badge); the link itself is the route's. */
export function withShareLook(share: ShareSettings, patch: SharePatch): ShareSettings {
  const next: ShareSettings = { ...share };
  if (patch.badge !== undefined) next.badge = patch.badge;
  if (patch.view === "page") next.view = "page";
  // Board is how every link showed before: kept as no say at all, so an older link and a switched-back one match
  else if (patch.view === "board") delete next.view;
  if (patch.line !== undefined) {
    if (patch.line) next.line = patch.line;
    else delete next.line;
  }
  if (patch.page !== undefined) {
    if (patch.page) next.page = patch.page;
    else delete next.page;
  }
  return next;
}

// ---------------------------------------------------------------------------------------------- one page of a doc

/** The pages a page link shows: that page and every page inside it, or none when the page is gone (then the link shows nothing). */
export function pagesInScope(pages: Pick<Entry, "id" | "parent">[], root: string): Set<string> {
  if (!pages.some((p) => p.id === root)) return new Set();
  return new Set([root, ...descendants(pages, root).map((p) => p.id)]);
}

/** A page body with each link to a page outside keep left as plain words, so it names no page the link does not show. */
function withoutOutsideLinks(n: unknown, keep: Set<string>): unknown {
  if (!n || typeof n !== "object") return n;
  const node = n as { marks?: { type?: string; attrs?: { href?: unknown } }[]; content?: unknown[] };
  let out = node;
  if (Array.isArray(node.marks))
    out = { ...out, marks: node.marks.filter((m) => !(m?.type === "link" && typeof m.attrs?.href === "string" && m.attrs.href.startsWith("page:") && !keep.has(m.attrs.href.slice(5)))) };
  return Array.isArray(node.content) ? { ...out, content: node.content.map((c) => withoutOutsideLinks(c, keep)) } : out;
}

/** A page as a page link's visitor gets it: the shared page is the top of the site it shows, and no link in it leads outside. */
export function scopedPage<E extends Pick<Entry, "id" | "parent" | "data">>(e: E, root: string, keep: Set<string>): E {
  const body = e.data.body ? withoutOutsideLinks(e.data.body, keep) : e.data.body;
  return { ...e, parent: e.id === root ? null : e.parent, data: body === e.data.body ? e.data : { ...e.data, body } };
}

// ---------------------------------------------------------------------------------------------- a doc site's pages

/** A page's title as an address: lower case letters, numbers and dashes, or "" when it has none of them. */
export function slugOf(title: string): string {
  return title
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
}

/**
 * Each page's address on the web page, from its title, in the site's own order (the tree read top to bottom): the
 * first page is the home page, at the link itself (""), and a title two pages share gets -2, -3 on the later ones.
 * A title with no letters or numbers (all emoji, or another script) goes by the page's id.
 */
export function pageAddresses(pages: { id: string; title: string }[]): Map<string, string> {
  const out = new Map<string, string>();
  const used = new Set<string>([""]);
  pages.forEach((p, i) => {
    if (i === 0) {
      out.set(p.id, "");
      return;
    }
    const base = slugOf(p.title) || p.id.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    let s = base;
    for (let n = 2; used.has(s); n++) s = `${base}-${n}`;
    used.add(s);
    out.set(p.id, s);
  });
  return out;
}

/** The page an address opens: its title's address, else a page's id (an address kept from before a rename works while the id does). */
export function pageAt(addresses: Map<string, string>, part: string): string | null {
  const want = decodeURIComponent(part).replace(/\/+$/, "").toLowerCase();
  for (const [id, a] of addresses) if (a === want) return id;
  for (const id of addresses.keys()) if (id.toLowerCase() === want) return id;
  return null;
}

/** A link's address and the page part after it: /s/<slug>[/<page>]. */
export const SHARE_PATH = /^\/s\/([a-z0-9-]{3,40})(?:\/([^/?#]{1,120}))?\/?$/;

// ---------------------------------------------------------------------------------------------- the head of a page load

/** What a link's page load may say in its head, and nothing more: the board's public name and line, and who it is by. */
export interface ShareHead {
  name: string;
  emoji: string | null;
  line: string | null;
  /** the owner's public name, as the shared view shows it after "by", or null */
  owner: string | null;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** The words a link preview shows: the board's name, and its line or whose board it is. */
export function headWords(h: ShareHead): { title: string; description: string } {
  const name = cleanShareLine(h.name) ?? "Untitled board";
  const emoji = h.emoji ? cleanShareLine(h.emoji) : null;
  const owner = h.owner ? cleanShareLine(h.owner) : null;
  return {
    title: emoji ? `${emoji} ${name}` : name,
    description: cleanShareLine(h.line) ?? (owner ? `${owner}'s board on Context Board` : "A board on Context Board"),
  };
}

/**
 * The tags a link's page load gets in its head, every value escaped: a title, a description, and Open Graph and X's
 * card, which Slack, iMessage, WhatsApp, X and LinkedIn read for a preview. url is the link's own address, which they
 * keep as the page's (where index.html names the front page for search).
 */
export function headTags(h: ShareHead, url: string, image?: string | null): string {
  const { title, description } = headWords(h);
  const meta = (attr: "name" | "property", key: string, value: string) => `<meta ${attr}="${key}" content="${escapeHtml(value)}" />`;
  return [
    `<title>${escapeHtml(title)}</title>`,
    meta("name", "description", description),
    `<link rel="canonical" href="${escapeHtml(url)}" />`,
    meta("property", "og:title", title),
    meta("property", "og:description", description),
    meta("property", "og:site_name", "Context Board"),
    meta("property", "og:type", "website"),
    meta("property", "og:url", url),
    ...(image ? [meta("property", "og:image", image), meta("property", "og:image:width", "1200"), meta("property", "og:image:height", "630")] : []),
    meta("name", "twitter:card", image ? "summary_large_image" : "summary"),
    meta("name", "twitter:title", title),
    meta("name", "twitter:description", description),
    ...(image ? [meta("name", "twitter:image", image)] : []),
  ].join("\n    ");
}
